import { useCallback } from 'react';
import { useSweepApi } from '../sweep';
import { OpenAiResponse } from '../../components/parser/ParserTypes';
import { BackendDocumentationMetadataTypesTab } from '../../components/documentation/tabs/types';
import {
  BaseElementProperties,
  LayoutProperties,
  SearchResult,
  ContentSearchResult,
  ConsumersOrDependencies,
} from '@server/documentation';
import { ObjectParsedDataByType } from '@server/parser';
import { SearchResponseWithConfigurationItems } from '../../components/documentation/universal-search/types';
import { AiChatDto } from '../../components/common/ai-chat/aiChatTypes';
import buildURLQuery from '../../lib/buildURLQuery';
import { AiChatConfigurationContext, AiChatFieldContext } from '@server/ai';
import { createConfigurationItemFromParserResponseItem } from '../../components/parser/parserUtils';

interface AiChatBasePayload {
  sessionId: string;
  message: string;
  isFirstMessage: boolean;
  crmOrgId: string;
}

export interface AiChatFieldPayload extends AiChatBasePayload {
  context: AiChatFieldContext;
}

export interface AiChatConfigurationPayload extends AiChatBasePayload {
  context: AiChatConfigurationContext;
}

const transformSearchResponse = (
  response: ContentSearchResult | SearchResult,
): SearchResponseWithConfigurationItems => {
  const _response = response;
  delete _response?.files; //by product decision files are not supported atm

  return Object.entries(_response).reduce((acc, [key, value]) => {
    acc[key as keyof SearchResponseWithConfigurationItems] =
      value?.map((item) => {
        const metadataId = 'metadataId' in item ? item.metadataId : '';
        const namespace = 'namespace' in item ? item.namespace : '';
        const objectName = 'objectName' in item ? (item.objectName ?? '') : '';

        return createConfigurationItemFromParserResponseItem({
          keyType: key,
          responseItem: {
            ...item,
            metadataId,
            namespace,
            type: key,
          },
          objectApiName: objectName,
        });
      }) ?? [];
    return acc;
  }, {} as SearchResponseWithConfigurationItems);
};

export const useDocumentationApiFacade = () => {
  const sweepApi = useSweepApi();

  return {
    post_createObject: useCallback(
      async (objectName: string, { payload }: { payload: { favorite: boolean } }) => {
        const response = await sweepApi.post<ObjectParsedDataByType>(
          `/documentation/objects/${objectName}`,
          payload,
        );
        return response.data;
      },
      [sweepApi],
    ),
    patch_toggleObjectFavorite: useCallback(
      async (objectName: string, favorite: boolean) => {
        return await sweepApi.patch(`/documentation/objects/${objectName}`, {
          favorite,
        });
      },
      [sweepApi],
    ),
    get_metadataTypes: useCallback(async (): Promise<BackendDocumentationMetadataTypesTab[]> => {
      const response = await sweepApi.get<BackendDocumentationMetadataTypesTab[]>(
        `/documentation/metadataTypes`,
      );
      return response.data;
    }, [sweepApi]),
    get_metadataData: useCallback(
      async (metadataTypeName: string) => {
        const response = await sweepApi.get<BaseElementProperties[]>(
          `/documentation/metadataTypes/${metadataTypeName}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    get_layout: useCallback(
      async (metadataId: string) => {
        const response = await sweepApi.get<LayoutProperties>(
          `/documentation/layouts/${metadataId}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    post_metadata: useCallback(
      async (metadataTypeName: string) => {
        await sweepApi.post(`/documentation/metadataTypes`, { metadataTypeName });
      },
      [sweepApi],
    ),
    delete_metadataType: useCallback(
      async (metadataTypeName: string) => {
        await sweepApi.delete(`/documentation/metadataTypes/${metadataTypeName}`);
      },
      [sweepApi],
    ),
    get_crmOrgConfigurationDependenciesFromSalto: useCallback(
      async ({ crmOrgId, metadataId }: { crmOrgId: string; metadataId: string }) => {
        const { data } = await sweepApi.get<ConsumersOrDependencies>(
          `/documentation/org/${crmOrgId}/configuration/dependencies?metadataId=${encodeURIComponent(metadataId)}`,
        );

        return data;
      },
      [sweepApi],
    ),
    get_crmOrgConfigurationConsumersFromSalto: useCallback(
      async ({ crmOrgId, metadataId }: { crmOrgId: string; metadataId: string }) => {
        const response = await sweepApi.get<ConsumersOrDependencies>(
          `/documentation/org/${crmOrgId}/configuration/consumers?metadataId=${encodeURIComponent(metadataId)}`,
        );

        return response.data;
      },
      [sweepApi],
    ),
    get_crmOrgFieldConsumerFromSalto: useCallback(
      async ({
        crmOrgId,
        objectName,
        fieldName,
        metadataId,
      }: {
        crmOrgId: string;
        objectName: string;
        fieldName: string;
        metadataId: string;
      }) => {
        const searchParams = buildURLQuery({
          objectName,
          fieldName,
          activeOnly: false,
          metadataId,
        });
        const response = await sweepApi.get<ConsumersOrDependencies>(
          `/documentation/org/${crmOrgId}/field-consumers?${searchParams}`,
        );
        return response.data;
      },
      [sweepApi],
    ),
    post_aiConfigurationChat: useCallback(
      async (payload: AiChatConfigurationPayload) => {
        const { crmOrgId, ..._payload } = payload;
        const response = await sweepApi.post(`/documentation/org/${crmOrgId}/ai-chat`, _payload);
        return response.data as AiChatDto;
      },
      [sweepApi],
    ),
    post_aiFieldChat: useCallback(
      async (payload: AiChatFieldPayload) => {
        const { crmOrgId, ..._payload } = payload;
        const response = await sweepApi.post(`/documentation/org/${crmOrgId}/ai-chat`, _payload);
        return response.data as AiChatDto;
      },
      [sweepApi],
    ),
    post_crmOrgGenerateConfigurationGpt: useCallback(
      async ({ crmOrgId, payload }: { crmOrgId: string; payload: { metadataId: string } }) => {
        const response = await sweepApi.post(
          `/documentation/org/${crmOrgId}/generate-configuration-gpt`,
          payload,
        );
        return response.data as OpenAiResponse;
      },
      [sweepApi],
    ),
    get_searchNameAndLabel: useCallback(
      async ({ crmOrgId, searchTxt }: { crmOrgId: string; searchTxt: string }) => {
        const response = await sweepApi.get(
          `/documentation/org/${crmOrgId}/search?searchText=${encodeURIComponent(searchTxt)}`,
        );
        return transformSearchResponse(response.data);
      },
      [sweepApi],
    ),
    get_searchContent: useCallback(
      async ({ crmOrgId, searchTxt }: { crmOrgId: string; searchTxt: string }) => {
        const response = await sweepApi.get(
          `/documentation/org/${crmOrgId}/search-content?searchText=${encodeURIComponent(searchTxt)}`,
        );
        return transformSearchResponse(response.data);
      },
      [sweepApi],
    ),
    get_NaclFile: useCallback(
      async ({ crmOrgId, metadataId }: { crmOrgId: string; metadataId: string }) => {
        const response = await sweepApi.get(
          `/documentation/org/${crmOrgId}/nacl-file?metadataId=${metadataId}`,
        );
        return response.data as string;
      },
      [sweepApi],
    ),
  };
};
